import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Film,
  Crosshair,
  SkipBack,
  SkipForward,
  Flag,
  X,
  Check,
  HelpCircle,
  Ban,
  Clock,
  AlertTriangle,
  PauseCircle,
  Radio,
  MinusCircle,
  Plus,
  Save,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { t as translate } from "i18n";
import {
  REVIEW_LABEL,
  recordKey,
  needsCheck,
  gateAgreement,
  videoTimeForSessionMs,
  sessionMsForVideoTime,
  fmtVideoTime,
  makeSync,
  mergeSourceRecords,
  multiProgress,
  reviewProgress,
  multiGateScore,
  cueList,
  crossedCue,
} from "utils/videoReview";
import {
  fmtElapsed,
  fmtLocalTime,
  timeTooltip,
  tzOffsetLabel,
} from "utils/timeDisplay";

// One frame at 30 fps. Nudging by a frame is what makes the sync accurate
// enough to trust: an impact lasts a few milliseconds, so eyeballing the play
// head to the nearest second leaves every later scrub up to a second out.
const FRAME = 1 / 30;

// How close the playhead has to be for an impact to read as "happening now".
// `timeupdate` fires about four times a second, so anything tighter would
// blink past unseen between ticks.
const LIVE_WINDOW_S = 0.6;

// Track colours arrive as hex from the compare view; tinting a chip needs the
// same hue at low alpha.
const hexAlpha = (hex, a) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(
    hex.slice(3, 5),
    16
  )},${parseInt(hex.slice(5, 7), 16)},${a})`;

const VERDICT = {
  [REVIEW_LABEL.HEADER]: {
    i18n: "header",
    color: "#059669",
    key: "H",
    Icon: Check,
  },
  [REVIEW_LABEL.NOT_HEADER]: {
    i18n: "notHeader",
    color: "#DC2626",
    key: "N",
    Icon: X,
  },
  // A real impact that was not a header, and the device firing at nothing, are
  // different failures with different fixes -- so they are different verdicts.
  [REVIEW_LABEL.FALSE_POSITIVE]: {
    i18n: "falsePositive",
    color: "#9333EA",
    key: "F",
    Icon: Ban,
  },
  [REVIEW_LABEL.PRE_SESSION]: {
    i18n: "preSession",
    color: "#6B7280",
    key: "P",
    Icon: Clock,
  },
  [REVIEW_LABEL.UNSURE]: {
    i18n: "unsure",
    color: "#B45309",
    key: "U",
    Icon: HelpCircle,
  },
};
// Display text for a verdict, translated at render time.
const verdictText = (t, v) =>
  t(`analysis.videoReview.verdict.${VERDICT[v].i18n}`);
const verdictShort = (t, v) =>
  t(`analysis.videoReview.verdictShort.${VERDICT[v].i18n}`);

const VERDICT_ORDER = [
  REVIEW_LABEL.HEADER,
  REVIEW_LABEL.NOT_HEADER,
  REVIEW_LABEL.FALSE_POSITIVE,
  REVIEW_LABEL.PRE_SESSION,
  REVIEW_LABEL.UNSURE,
];
const VERDICT_ICON = Object.fromEntries(
  VERDICT_ORDER.map((v) => [v, VERDICT[v].Icon])
);
// Which reviewProgress() tally each verdict reads from. A lookup rather than
// a chain, so adding a verdict cannot silently fall through to the last one.
const PROGRESS_FIELD = {
  [REVIEW_LABEL.HEADER]: "headers",
  [REVIEW_LABEL.NOT_HEADER]: "notHeaders",
  [REVIEW_LABEL.FALSE_POSITIVE]: "falsePositives",
  [REVIEW_LABEL.PRE_SESSION]: "preSession",
  [REVIEW_LABEL.UNSURE]: "unsure",
};
const KEY_TO_VERDICT = Object.fromEntries(
  VERDICT_ORDER.map((v) => [VERDICT[v].key.toLowerCase(), v])
);

// The row has no room for "too narrow + no rotation", and the full wording is
// in the record detail anyway.
// Gate verdict tokens (as written by utils/wearableExport) -> key suffix under
// analysis.videoReview.gateShort.
const GATE_SHORT = {
  pass: "pass",
  "too narrow": "narrow",
  "too wide": "wide",
  "no rotation": "noRotation",
};
function gateShort(label) {
  if (!label || label === "unknown" || label === "not evaluated") return null;
  return label
    .split(" + ")
    .map((p) =>
      GATE_SHORT[p]
        ? translate(`analysis.videoReview.gateShort.${GATE_SHORT[p]}`)
        : p
    )
    .join("+");
}

// ── Session timeline ────────────────────────────────────────────────
// Every impact gets its own tick. The shared strip clusters neighbours so a
// dense burst stays clickable, but here each impact must be individually
// reachable and individually coloured -- a cluster would hide exactly the
// back-to-back headers that are hardest to judge.

const ReviewTimeline = ({
  items,
  sources,
  missed,
  activeKey,
  playheadMs,
  onPick,
}) => {
  const { t } = useTranslation();
  const win = useMemo(() => {
    const ts = items.map((i) => i.tMs).filter(Number.isFinite);
    if (!ts.length) return null;
    const t0 = Math.min(...ts);
    const t1 = Math.max(...ts);
    // A session with one impact, or several at the same instant, has no span
    // to divide by. Give it a nominal one so the tick lands mid-strip.
    const span = t1 - t0 || 1000;
    return { t0: t0 - span * 0.02, span: span * 1.04, first: t0, last: t1 };
  }, [items]);
  // The strip had no scale at all: a tick two thirds along could be four
  // minutes in or forty. Ends only -- anything denser competes with the ticks.
  const epoch = items.some((i) => i.rec.timestampIsEpoch);
  const edge = (ms) =>
    epoch
      ? fmtLocalTime(ms)
      : `T+${fmtElapsed(Math.max(0, ms - (win?.first ?? 0)), {
          compact: true,
        })}`;

  if (!win) return null;
  const xOf = (ms) => ((ms - win.t0) / win.span) * 100;
  const multi = sources.length > 1;
  // One lane per unit when several are loaded: two athletes heading the same
  // ball land at the same instant, and a single strip would draw one tick on
  // top of the other -- hiding exactly the agreement that confirms it.
  const laneH = multi ? Math.max(16, Math.min(30, 96 / sources.length)) : 44;

  const Ticks = ({ src }) => {
    const rows = src ? items.filter((i) => i.srcId === src.id) : items;
    return rows.map((it) => {
      const label = it.src.review.labels[recordKey(it.rec)]?.label;
      const active = it.key === activeKey;
      const h = Math.round(
        (multi ? 4 : 6) +
          Math.min(1, (it.rec.peakMagG ?? 0) / 50) * (multi ? laneH - 8 : 14)
      );
      return (
        <button
          key={it.key}
          onClick={() => onPick(it)}
          title={`${multi ? `${it.src.name} · ` : ""}#${it.rec.impactId} · ${(
            it.rec.peakMagG ?? 0
          ).toFixed(1)} g${
            label && VERDICT[label] ? ` · ${verdictText(t, label)}` : ""
          }`}
          className="absolute -translate-x-1/2 rounded-sm transition-all duration-150 hover:opacity-70"
          style={{
            left: `${xOf(it.tMs)}%`,
            bottom: multi ? 2 : 26,
            height: h,
            width: active ? 4 : 2,
            // Unlabelled ticks carry the unit's colour so a lane reads as a
            // unit; a verdict overrides it, because the verdict is the point.
            backgroundColor: label
              ? VERDICT[label].color
              : multi
              ? hexAlpha(it.src.color, 0.55)
              : "#C4C8CE",
            outline: active ? "1px solid #111827" : "none",
            outlineOffset: 1,
          }}
        />
      );
    });
  };

  return (
    <div className="flex-shrink-0 border-t border-[#F3F4F6] px-3 pb-2.5 pt-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
          {t("analysis.videoReview.sessionTimeline")}
        </span>
        <span className="flex items-center gap-2.5 text-[10px] text-[#9CA3AF]">
          {VERDICT_ORDER.map((v) => (
            <span key={v} className="flex items-center gap-1">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: VERDICT[v].color }}
              />
              {verdictText(t, v)}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#7C3AED]" />
            {t("analysis.videoReview.missed")}
          </span>
        </span>
      </div>

      <div className="relative rounded bg-[#FAFAF9]">
        {multi ? (
          sources.map((src) => (
            <div
              key={src.id}
              style={{ height: laneH }}
              className="relative border-b border-[#F1F0EE] last:border-b-0"
            >
              <span
                className="pointer-events-none absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-sm px-1 text-[8px] font-semibold leading-tight"
                style={{
                  color: src.color,
                  backgroundColor: hexAlpha(src.color, 0.1),
                }}
              >
                {src.name}
              </span>
              <Ticks src={src} />
              {missed
                .filter((m) => m.src.id === src.id)
                .map((m) => (
                  <span
                    key={`${m.src.id}:${m.index}`}
                    title={
                      m.note
                        ? t("analysis.videoReview.missedHeaderWithNote", {
                            note: m.note,
                          })
                        : t("analysis.videoReview.missedHeader")
                    }
                    className="absolute top-0 h-1.5 w-0.5 -translate-x-1/2 rounded-sm bg-[#7C3AED]"
                    style={{ left: `${xOf(m.tMs)}%` }}
                  />
                ))}
            </div>
          ))
        ) : (
          <div className="relative h-11">
            <div className="absolute inset-x-0 top-[26px] h-px bg-[#E5E1DC]" />
            <Ticks src={null} />
            {/* headers the device never fired on, below the axis */}
            {missed.map((m, i) => (
              <span
                key={i}
                title={
                  m.note
                    ? t("analysis.videoReview.missedHeaderWithNote", {
                        note: m.note,
                      })
                    : t("analysis.videoReview.missedHeader")
                }
                className="absolute h-2.5 w-0.5 -translate-x-1/2 rounded-sm bg-[#7C3AED]"
                style={{ left: `${xOf(m.tMs)}%`, top: 27 }}
              />
            ))}
          </div>
        )}

        {/* where the video is now — spans every lane */}
        {playheadMs != null && (
          <div
            className="pointer-events-none absolute inset-y-0 z-20 w-px bg-[#E07A3A]"
            style={{ left: `${Math.min(100, Math.max(0, xOf(playheadMs)))}%` }}
          >
            <span className="absolute -left-[3px] top-0 h-1.5 w-1.5 rounded-full bg-[#E07A3A]" />
          </div>
        )}
      </div>

      <div className="mt-0.5 flex justify-between text-[9px] tabular-nums text-[#C4C8CE]">
        <span title={t("analysis.videoReview.firstRecord")}>
          {edge(win.first)}
        </span>
        <span title={t("analysis.videoReview.lastRecord")}>
          {edge(win.last)}
        </span>
      </div>
    </div>
  );
};

// ── Impact list ─────────────────────────────────────────────────────

const ImpactRow = ({
  item,
  verdict,
  active,
  multi,
  showUnit = true,
  live = false,
  t0,
  onPick,
}) => {
  const { t } = useTranslation();
  const record = item.rec;
  const v = verdict ? VERDICT[verdict] : null;
  const gate = gateShort(record.gateFlagsLabel);
  const gatePassed = record.gateFlagsLabel === "pass";
  const agreement = gateAgreement(record, verdict);
  return (
    <button
      onClick={() => onPick(item)}
      data-rowkey={item.key}
      title={
        multi
          ? t("analysis.videoReview.impactTitleUnit", {
              unit: item.src.name,
              id: record.impactId,
            })
          : t("analysis.videoReview.impactTitle", { id: record.impactId })
      }
      className={`flex w-full items-center gap-2 border-b border-[#F3F4F6] px-2.5 py-1.5 text-left transition duration-150 last:border-b-0 ${
        active ? "bg-[#FFF7ED]" : "hover:bg-[#FAFAFA]"
      }`}
      style={
        // "Happening now" is drawn in the unit's own colour, so when two
        // columns light together you can see it without reading a word. The
        // whole row tints; the marker column below carries the shape.
        live ? { backgroundColor: hexAlpha(item.src.color, 0.16) } : undefined
      }
    >
      {/* An unchecked impact is the point of the list, so it gets the only
          mark that draws the eye. Everything decided goes quiet. */}
      <span className="w-2 flex-shrink-0">
        {live ? (
          <span
            title={t("analysis.videoReview.videoOnImpact")}
            className="block h-2 w-2 rounded-full"
            style={{ backgroundColor: item.src.color }}
          />
        ) : (
          !verdict && (
            <span
              title={t("analysis.videoReview.stillToCheck")}
              className="block h-1.5 w-1.5 rounded-full bg-[#E07A3A]"
            />
          )
        )}
      </span>
      {/* Which unit fired. With one file this column is noise; with five it is
          the first thing you need, because two units heading the same ball is
          the confirmation and one unit alone is usually an artefact. */}
      {multi && showUnit && (
        <span
          className="w-16 flex-shrink-0 truncate rounded px-1 py-px text-[9px] font-semibold"
          style={{
            color: item.src.color,
            backgroundColor: hexAlpha(item.src.color, 0.1),
          }}
        >
          {item.src.name}
        </span>
      )}
      <span className="w-7 flex-shrink-0 text-[11px] tabular-nums text-[#9CA3AF]">
        #{record.impactId}
      </span>
      <span className="w-12 flex-shrink-0 text-right text-[12px] font-semibold tabular-nums text-[#374151]">
        {(record.peakMagG ?? 0).toFixed(1)} g
      </span>
      {/* What the firmware would have decided, before you looked. */}
      <span className="w-14 flex-shrink-0 text-center">
        {gate && (
          <span
            title={t("analysis.videoReview.dryRunGate", {
              label: record.gateFlagsLabel,
            })}
            className={`inline-block rounded px-1 py-px text-[9px] font-medium ${
              gatePassed
                ? "bg-[#ECFDF5] text-[#047857]"
                : "bg-[#FFFBEB] text-[#B45309]"
            }`}
          >
            {gate}
          </span>
        )}
      </span>
      {/* The clock the session actually happened on, where the device knew
          it. T+ alone is fine inside one export and useless the moment you
          are lining a record up against footage, a race schedule or another
          unit's file -- all of which are stamped in wall time. */}
      <span
        className="flex-1 truncate text-right text-[10px] tabular-nums text-[#C4C8CE]"
        title={timeTooltip(record.timestampMs, record.timestampIsEpoch, t0)}
      >
        {record.timestampIsEpoch && (
          <span className="mr-1.5 text-[#9CA3AF]">
            {fmtLocalTime(record.timestampMs)}
          </span>
        )}
        T+{fmtElapsed(Math.max(0, item.tMs - t0), { compact: true })}
      </span>
      {/* A header the gate would have thrown away is the one disagreement
          worth interrupting for -- it is the error that loses real data. */}
      <span className="w-3 flex-shrink-0">
        {agreement === "would_drop" && (
          <AlertTriangle
            size={11}
            className="text-[#B45309]"
            title={t("analysis.videoReview.gateWouldDrop")}
          />
        )}
      </span>
      <span className="flex w-14 flex-shrink-0 items-center justify-end gap-1">
        {v && (
          <>
            <v.Icon size={11} style={{ color: v.color }} />
            <span className="text-[10px]" style={{ color: v.color }}>
              {verdictShort(t, verdict)}
            </span>
          </>
        )}
      </span>
    </button>
  );
};

/** A header the device never fired on. It has no record, no peak and no gate
 *  verdict -- that IS the finding -- but it belongs in the list at the moment
 *  it happened, because a checklist that only lists what the device noticed
 *  can measure precision and never recall. */
const MissedRow = ({ entry, t0, onPick, onRemove }) => {
  const { t } = useTranslation();
  const time = fmtElapsed(Math.max(0, entry.tMs - t0), { compact: true });
  return (
    <div
      data-rowkey={`missed:${entry.src.id}:${entry.index}`}
      title={
        entry.note
          ? t("analysis.videoReview.missedAtWithNote", {
              time,
              note: entry.note,
            })
          : t("analysis.videoReview.missedAt", { time })
      }
      className="flex w-full items-center gap-2 border-b border-[#F3F4F6] bg-[#F7F5FF] px-2.5 py-1.5 text-left last:border-b-0"
    >
      <button
        onClick={() => onPick(entry)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <Flag size={11} className="flex-shrink-0 text-[#7C3AED]" />
        <span className="text-[11px] font-medium text-[#6D28D9]">
          {t("analysis.videoReview.missed")}
        </span>
        <span className="min-w-0 flex-1 truncate text-[10px] text-[#9CA3AF]">
          {entry.note || t("analysis.videoReview.notDetected")}
        </span>
        <span className="flex-shrink-0 text-[10px] tabular-nums text-[#C4C8CE]">
          T+{fmtElapsed(Math.max(0, entry.tMs - t0), { compact: true })}
        </span>
      </button>
      <button
        onClick={onRemove}
        title={t("common.actions.remove")}
        className="flex-shrink-0 text-[#C4B5FD] transition duration-150 hover:text-[#DC2626]"
      >
        <X size={10} />
      </button>
    </div>
  );
};

// ── Panel ───────────────────────────────────────────────────────────

/**
 * Review a session against footage of it.
 *
 * `sources` is one entry per loaded export -- a session is filmed once but
 * worn by several athletes, so N files share one video and one sync point.
 * Each source keeps its OWN review object and its own `onReview`, which is
 * what lets every file be saved back carrying its own tagging.
 *
 * A single file may instead pass `records`/`review`/`onReview` directly.
 */
const VideoReview = ({
  sources,
  records,
  review,
  onReview,
  scrubTo,
  onScrubHandled,
  selectedRecord,
  onSelectRecord,
  onVideoLoaded,
  onAddUnits,
  addBusy = false,
  fill = false,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [duration, setDuration] = useState(NaN);
  const [now, setNow] = useState(0);
  const [portrait, setPortrait] = useState(false);
  const [anchorKey, setAnchorKey] = useState("");
  const [note, setNote] = useState("");
  // Open only while there is nothing to open it for. A session reopened with
  // its sync already in the file should land ready to work, not on a form.
  const [showSync, setShowSync] = useState(false);
  const [filter, setFilter] = useState("all");
  const [unitFilter, setUnitFilter] = useState("all");
  // What the playhead does when it reaches an impact. Watching the session
  // once and letting the impacts come to you is a different job from clicking
  // a row and jumping -- and it is the one that catches what the device MISSED,
  // because you are actually watching the football rather than a list.
  const [playMode, setPlayMode] = useState("follow");
  const [autoPaused, setAutoPaused] = useState(null);
  const prevTRef = useRef(0);
  const listRef = useRef(null);

  const srcs = useMemo(
    () =>
      sources?.length
        ? sources
        : [
            {
              id: "_",
              name: null,
              color: "#E07A3A",
              records: records || [],
              review,
              onReview,
            },
          ],
    [sources, records, review, onReview]
  );
  const multi = srcs.length > 1;
  const items = useMemo(() => mergeSourceRecords(srcs), [srcs]);
  const progress = useMemo(() => multiProgress(srcs), [srcs]);
  const t0 = items[0]?.tMs ?? 0;
  // A bare "15:40:22" is ambiguous without a date, and half the exports on
  // this bench were captured by a device whose clock was never synced. Both
  // facts get stated once, up top, rather than repeated on every row.
  const epochItem = items.find((i) => i.rec.timestampIsEpoch) ?? null;
  const allEpoch =
    items.length > 0 && items.every((i) => i.rec.timestampIsEpoch);

  // The sync point is a property of the footage, not of any one unit, so it is
  // written into EVERY source. That way whichever file you reopen later comes
  // back already lined up against the video it was reviewed with.
  const sync = srcs[0].review.sync;
  const setSyncAll = useCallback(
    (next) => {
      for (const s of srcs) s.onReview((r) => ({ ...r, sync: next }));
    },
    [srcs]
  );

  const shown = useMemo(() => {
    let list = items;
    if (unitFilter !== "all") list = list.filter((i) => i.srcId === unitFilter);
    if (filter === "todo")
      return list.filter((i) => needsCheck(i.rec, i.src.review));
    if (filter === "headers")
      return list.filter(
        (i) =>
          i.src.review.labels[recordKey(i.rec)]?.label === REVIEW_LABEL.HEADER
      );
    return list;
  }, [items, filter, unitFilter]);

  // Missed headers belong to a unit -- the device missed it -- but they are
  // reviewed against one shared clock, so they merge for display.
  const missed = useMemo(
    () =>
      srcs
        .flatMap((s) =>
          (s.review.missed || []).map((m, i) => ({
            ...m,
            src: s,
            index: i,
            tMs: m.sessionMs + (s.offsetMs || 0),
          }))
        )
        .sort((a, b) => a.tMs - b.tMs),
    [srcs]
  );

  // One column per loaded file, in load order. A unit with nothing left to
  // check still gets its column -- an empty column is information.
  const columns = useMemo(
    () =>
      srcs
        .filter((s) => unitFilter === "all" || s.id === unitFilter)
        .map((src) => ({
          src,
          items: shown.filter((i) => i.srcId === src.id),
          // Interleaved by time, not appended: a missed header is a hole in
          // the sequence, and it only reads as one where the hole is. Hidden
          // under "To check" -- it has already been decided, by definition.
          rows: [
            ...shown.filter((i) => i.srcId === src.id),
            ...(filter === "todo"
              ? []
              : missed
                  .filter((m) => m.src.id === src.id)
                  .map((m) => ({ ...m, missedEntry: true }))),
          ].sort((a, b) => a.tMs - b.tMs),
        })),
    [srcs, shown, unitFilter, missed, filter]
  );

  // How the firmware's dry-run gates are actually doing against what you saw.
  const score = useMemo(() => multiGateScore(srcs), [srcs]);

  useEffect(() => () => src && URL.revokeObjectURL(src), [src]);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setFileName(file.name);
    for (const s of srcs) s.onReview((r) => ({ ...r, videoName: file.name }));
    onVideoLoaded?.(true);
  };

  const seek = useCallback((sec) => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(sec)) return;
    v.currentTime = Math.max(0, sec);
  }, []);

  useEffect(() => {
    if (scrubTo == null || !sync) return;
    const vt = videoTimeForSessionMs(scrubTo, sync);
    if (vt != null) seek(vt);
    onScrubHandled?.();
  }, [scrubTo, sync, seek, onScrubHandled]);

  const selected = useMemo(
    () => items.find((i) => i.rec === selectedRecord) ?? null,
    [items, selectedRecord]
  );

  const pick = useCallback(
    (item) => {
      onSelectRecord?.(item.rec, item.src);
      if (sync) seek(videoTimeForSessionMs(item.tMs, sync));
    },
    [onSelectRecord, seek, sync]
  );

  const selKey = selected?.key ?? null;
  const selLabel = selected
    ? selected.src.review.labels[recordKey(selected.rec)]?.label ??
      REVIEW_LABEL.UNREVIEWED
    : null;

  const label = useCallback(
    (value) => {
      if (!selected) return;
      const k = recordKey(selected.rec);
      selected.src.onReview((r) => {
        const prev = r.labels[k] || {};
        const next = {
          ...prev,
          label: value === prev.label ? REVIEW_LABEL.UNREVIEWED : value,
        };
        if (next.label === REVIEW_LABEL.UNREVIEWED && !next.note) {
          const labels = { ...r.labels };
          delete labels[k];
          return { ...r, labels };
        }
        return { ...r, labels: { ...r.labels, [k]: next } };
      });
    },
    [selected]
  );

  const step = useCallback(
    (delta) => {
      const i = shown.findIndex((x) => x.key === selKey);
      const next = shown[(i === -1 ? 0 : i) + delta];
      if (next) pick(next);
    },
    [shown, selKey, pick]
  );

  // Everything before the session started is usually a contiguous run of kit
  // handling. Scoped to the selected unit: another athlete's device was picked
  // up at its own time, and sweeping all of them would invent verdicts.
  const markEarlierPreSession = useCallback(() => {
    if (!selected) return;
    const keys = items
      .filter((i) => i.srcId === selected.srcId && i.tMs <= selected.tMs)
      .map((i) => recordKey(i.rec));
    selected.src.onReview((r) => {
      const labels = { ...r.labels };
      for (const k of keys) {
        labels[k] = { ...(labels[k] || {}), label: REVIEW_LABEL.PRE_SESSION };
      }
      return { ...r, labels };
    });
  }, [items, selected]);

  const nextUnreviewed = useCallback(() => {
    const from = items.findIndex((x) => x.key === selKey) + 1;
    const todo = (i) => needsCheck(i.rec, i.src.review);
    const found = items.slice(from).find(todo) ?? items.find(todo);
    if (found) pick(found);
  }, [items, selKey, pick]);

  // Keyboard: labelling ~100 impacts by mouse alone is the slow part. Ignored
  // while typing a note, so a "u" in a note is not a verdict.
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "m") markMissedRef.current?.();
      else if (KEY_TO_VERDICT[k]) label(KEY_TO_VERDICT[k]);
      else if (e.key === "ArrowDown") step(1);
      else if (e.key === "ArrowUp") step(-1);
      else if (k === " ") {
        const v = videoRef.current;
        if (v) v.paused ? v.play() : v.pause();
      } else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [src, label, step]);

  // ── Watching, rather than clicking ──────────────────────────────
  const cues = useMemo(() => cueList(items, sync), [items, sync]);

  // Which impacts the playhead is ON right now. A window rather than an exact
  // hit because `now` only ticks a few times a second -- and because the point
  // is to see two units fire at the SAME moment, which is the strongest
  // evidence a contact was real rather than one device brushing something.
  const liveKeys = useMemo(() => {
    const out = new Set();
    for (const c of cues) {
      if (Math.abs(c.t - now) <= LIVE_WINDOW_S) out.add(c.item.key);
    }
    return out;
  }, [cues, now]);

  // The last impact each unit passed. Drives the scroll so a column keeps
  // pace with the tape between impacts, not only at them.
  const trailKeys = useMemo(() => {
    const last = new Map();
    for (const c of cues) {
      if (c.t <= now) last.set(c.item.srcId, c.item.key);
      else break; // cues are sorted
    }
    return [...last.values()];
  }, [cues, now]);

  // Driven by rAF, not `timeupdate`: the browser fires timeupdate about four
  // times a second, so a "stop at the impact" would overshoot by up to a
  // quarter of a second -- past the whole event, which lasts milliseconds.
  useEffect(() => {
    if (!src || !sync || playMode === "off" || cues.length === 0) {
      return undefined;
    }
    let raf = 0;
    let live = true;
    const tick = () => {
      const v = videoRef.current;
      if (v) {
        const cur = v.currentTime;
        const prev = prevTRef.current;
        if (cur < prev) {
          // Scrubbing back to look again is not a new crossing.
          prevTRef.current = cur;
        } else if (cur > prev) {
          const hit = crossedCue(cues, prev, cur);
          prevTRef.current = cur;
          if (hit) {
            // Select WITHOUT seeking: the tape is already there, and seeking
            // to where we already are makes the picture stutter.
            onSelectRecord?.(hit.item.rec, hit.item.src);
            if (playMode === "stop" && !v.paused) {
              v.pause();
              // Land exactly on the event rather than wherever the frame
              // boundary fell, so every stop is the same distance in.
              v.currentTime = hit.t;
              prevTRef.current = hit.t;
              setAutoPaused(hit.item.key);
            }
          }
        }
      }
      if (live) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      live = false;
      cancelAnimationFrame(raf);
    };
  }, [src, sync, playMode, cues, onSelectRecord]);

  // Keep the rows in view: the list is being driven from the video, and a
  // highlight scrolled off screen tells you nothing. Every column scrolls
  // itself, so five units all stay level with the tape.
  const scrollTo = useCallback((key) => {
    if (!key || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-rowkey="${key}"]`);
    // jsdom has no scrollIntoView, and neither do some embedded webviews.
    el?.scrollIntoView?.({ block: "nearest" });
  }, []);
  useEffect(() => scrollTo(selKey), [selKey, scrollTo]);
  const trailSig = trailKeys.join("|");
  useEffect(() => {
    for (const k of trailSig ? trailSig.split("|") : []) scrollTo(k);
  }, [trailSig, scrollTo]);

  const anchorItem = items.find((i) => i.key === anchorKey) ?? null;
  const setSyncHere = () => {
    const v = videoRef.current;
    if (!v || !anchorItem) return;
    setSyncAll(
      makeSync(anchorItem.tMs, v.currentTime, {
        anchorImpactId: anchorItem.rec.impactId,
        anchorUnit: anchorItem.src.name || null,
      })
    );
    setShowSync(false);
  };

  /** Line the selected impact's unit up with the frame now on screen. The
   *  master sync stays put: this is a per-unit trim, so the other units do
   *  not move under you while you fix one. */
  const alignSelectedUnit = () => {
    const v = videoRef.current;
    if (!v || !selected?.src.onOffset || !sync) return;
    const wantMs = sessionMsForVideoTime(v.currentTime, sync);
    if (!Number.isFinite(wantMs)) return;
    selected.src.onOffset(Math.round(wantMs - selected.rec.timestampMs));
  };

  // Held in a ref so the key handler does not re-bind on every frame of
  // playback: markMissed closes over the note and the selected unit.
  const markMissedRef = useRef(null);
  const markMissed = () => {
    const v = videoRef.current;
    if (!v || !sync) return;
    // Attributed to whichever unit is in hand: a missed header is one device
    // failing to fire, not a property of the session.
    const owner = selected?.src ?? srcs[0];
    const ms =
      sessionMsForVideoTime(v.currentTime, sync) - (owner.offsetMs || 0);
    owner.onReview((r) => ({
      ...r,
      missed: [...r.missed, { sessionMs: ms, note: note.trim() }].sort(
        (a, b) => a.sessionMs - b.sessionMs
      ),
    }));
    setNote("");
  };
  markMissedRef.current = markMissed;

  // ── Before there is any footage ──
  // Not "nothing here": the three steps, in order, with the one action that
  // starts them. Everything else on this screen is inert until step one.
  if (!src) {
    return (
      <div className="rounded-lg border border-dashed border-[#E3DFD9] bg-[#FDFDFC] px-5 py-5">
        <div className="flex flex-wrap items-start gap-5">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-[#111827]">
              {t("analysis.videoReview.introTitle")}
            </p>
            <p className="mt-1 max-w-prose text-[12px] leading-relaxed text-[#6B7280]">
              {multi
                ? t("analysis.videoReview.introBodyMulti", {
                    count: items.length,
                    units: srcs.length,
                  })
                : t("analysis.videoReview.introBody", { count: items.length })}
            </p>
            <ol className="mt-3 space-y-1.5 text-[11px] text-[#6B7280]">
              {[
                t("analysis.videoReview.stepAddFootage"),
                t("analysis.videoReview.stepLineUp"),
                t("analysis.videoReview.stepPlay"),
              ].map((step, i) => (
                <li key={step} className="flex gap-2">
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[9px] font-semibold text-[#6B7280]">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <label className="mt-4 inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-[#111827] px-3 py-2 text-[12px] font-medium text-white transition duration-150 hover:bg-[#374151]">
              <Film size={13} />
              {t("analysis.videoReview.addFootage")}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={pickFile}
              />
            </label>
            {onAddUnits && (
              <label className="ml-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-[12px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]">
                <Plus size={13} />
                {t("analysis.videoReview.addAnotherUnit")}
                <input
                  type="file"
                  accept=".json"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    onAddUnits(e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>
        </div>
      </div>
    );
  }

  const nowSessionMs = sync ? sessionMsForVideoTime(now, sync) : null;

  return (
    <div
      className={`overflow-hidden rounded-lg border border-[#E5E7EB] ${
        fill ? "flex h-full min-h-0 flex-col" : "mb-3"
      }`}
    >
      {/* The footage, and what is loaded against it. Two facts a reviewer
          checks constantly and one action they reach for mid-session, so they
          sit above the work rather than behind a menu. */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[#F3F4F6] px-3 py-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <Film size={12} className="flex-shrink-0 text-[#9CA3AF]" />
          <span className="truncate text-[11px] font-medium text-[#374151]">
            {fileName}
          </span>
        </span>
        {epochItem ? (
          <span
            className="whitespace-nowrap text-[10px] text-[#9CA3AF]"
            title={
              allEpoch
                ? t("analysis.videoReview.clockTitleAll")
                : t("analysis.videoReview.clockTitlePartial")
            }
          >
            {new Date(epochItem.rec.timestampMs).toLocaleDateString(undefined, {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}{" "}
            {tzOffsetLabel(epochItem.rec.timestampMs)}
            {!allEpoch && ` ${t("analysis.videoReview.partlyUnsynced")}`}
          </span>
        ) : (
          <span
            className="whitespace-nowrap text-[10px] text-[#B45309]"
            title={t("analysis.videoReview.noClockSyncTitle")}
          >
            {t("analysis.videoReview.noClockSync")}
          </span>
        )}
        <label className="cursor-pointer text-[10px] text-[#6B7280] underline decoration-dotted transition duration-150 hover:text-[#111827]">
          {t("analysis.videoReview.replaceVideo")}
          <input
            type="file"
            accept="video/*"
            className="hidden"
            onChange={pickFile}
          />
        </label>

        <span className="ml-auto flex items-center gap-3 text-[11px] tabular-nums">
          {/* A logged miss is review work too: gating the whole tally on
              `reviewed` meant marking one and being told nothing happened. */}
          {(progress.reviewed > 0 || progress.missed > 0) && (
            <span className="flex items-center gap-2">
              {VERDICT_ORDER.map((v) => {
                const n = PROGRESS_FIELD[v] ? progress[PROGRESS_FIELD[v]] : 0;
                return n > 0 ? (
                  <span
                    key={v}
                    className="font-semibold"
                    style={{ color: VERDICT[v].color }}
                  >
                    {t(`analysis.videoReview.tally.${VERDICT[v].i18n}`, { n })}
                  </span>
                ) : null;
              })}
              {progress.missed > 0 && (
                <span className="font-semibold text-[#7C3AED]">
                  {t("analysis.videoReview.tallyMissed", {
                    n: progress.missed,
                  })}
                </span>
              )}
            </span>
          )}
          <span className="text-[#9CA3AF]">
            {t("analysis.videoReview.checkedOf", {
              reviewed: progress.reviewed,
              total: progress.total,
            })}
          </span>
        </span>
      </div>

      {/* The units rail. Adding one used to mean leaving for another screen,
          which discarded the loaded video and the sync with it. It belongs
          here, beside the units it joins. */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-[#F3F4F6] bg-[#FAFAF9] px-3 py-1.5">
        {srcs.map((src) => {
          const p = reviewProgress(src.review, src.records || []);
          return (
            <span
              key={src.id}
              className="flex items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white py-0.5 pl-2 pr-1 text-[10px]"
            >
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ backgroundColor: src.color }}
              />
              <span className="font-semibold text-[#111827]">{src.name}</span>
              <span className="tabular-nums text-[#9CA3AF]">
                {p.reviewed}/{p.total}
              </span>
              {src.onSave && (
                <button
                  onClick={() => src.onSave()}
                  disabled={src.saving}
                  title={t("analysis.videoReview.writeBack", {
                    name: src.fileName || src.name,
                  })}
                  className="flex items-center gap-1 rounded px-1 py-0.5 font-medium text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#111827] disabled:opacity-40"
                >
                  <Save size={10} />
                  {src.saving
                    ? t("analysis.videoReview.saving")
                    : t("common.actions.save")}
                </button>
              )}
              {src.onRemove && srcs.length > 1 && (
                <button
                  onClick={() => src.onRemove()}
                  title={t("analysis.videoReview.closeUnit", {
                    name: src.name,
                  })}
                  className="rounded px-0.5 text-[#C4C8CE] transition duration-150 hover:text-[#DC2626]"
                >
                  <X size={10} />
                </button>
              )}
            </span>
          );
        })}
        {onAddUnits && (
          <label
            title={t("analysis.videoReview.addUnitHint")}
            className="flex cursor-pointer items-center gap-1 rounded-md border border-dashed border-[#D7D3CC] px-2 py-1 text-[10px] font-medium text-[#6B7280] transition duration-150 hover:border-[#E07A3A] hover:text-[#E07A3A]"
          >
            <Plus size={11} />
            {t("analysis.videoReview.addUnit")}
            <input
              type="file"
              accept=".json"
              multiple
              className="hidden"
              onChange={(e) => {
                onAddUnits(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
        )}
        {addBusy && (
          <span className="text-[10px] text-[#9CA3AF]">
            {t("analysis.videoReview.reading")}
          </span>
        )}
      </div>

      {/* Nothing on this screen works until the footage is lined up, so
          unsynced is not a collapsed disclosure: it is the only thing to do,
          numbered, with the reason stated. Once done it becomes one quiet
          line, because it is a one-off. */}
      {sync ? (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-[#F3F4F6] bg-[#FAFAF9] px-3 py-1.5 text-[11px]">
          <Crosshair size={11} className="text-[#059669]" />
          <span className="font-medium text-[#374151]">
            {t("analysis.videoReview.linedUp")}
          </span>
          <span className="text-[#9CA3AF]">
            {sync.anchorUnit
              ? t("analysis.videoReview.syncSummaryUnit", {
                  unit: sync.anchorUnit,
                  id: sync.anchorImpactId,
                  time: fmtVideoTime(sync.anchorVideoSec),
                })
              : t("analysis.videoReview.syncSummary", {
                  id: sync.anchorImpactId,
                  time: fmtVideoTime(sync.anchorVideoSec),
                })}
          </span>
          <button
            onClick={() => setShowSync((v) => !v)}
            className="text-[10px] text-[#6B7280] underline decoration-dotted transition duration-150 hover:text-[#111827]"
          >
            {showSync
              ? t("common.actions.done")
              : t("analysis.videoReview.adjust")}
          </button>
        </div>
      ) : (
        <div className="flex-shrink-0 border-b border-[#FDE68A] bg-[#FFFBEB] px-3 py-2">
          <p className="text-[11px] font-semibold text-[#92400E]">
            {t("analysis.videoReview.lineUpTitle")}
          </p>
          <p className="mt-0.5 text-[10px] leading-relaxed text-[#B45309]">
            {multi
              ? t("analysis.videoReview.lineUpBodyMulti")
              : t("analysis.videoReview.lineUpBody")}
          </p>
        </div>
      )}
      {(!sync || showSync) && (
        <div className="flex flex-shrink-0 flex-wrap items-end gap-2 border-b border-[#F3F4F6] bg-[#FAFAF9] px-3 py-2">
          <label className="text-[10px] text-[#6B7280]">
            {t("analysis.videoReview.anchorLabel")}
            <select
              value={anchorKey}
              onChange={(e) => setAnchorKey(e.target.value)}
              className="mt-0.5 block w-72 rounded border border-[#E5E7EB] bg-white px-1.5 py-1 text-[11px] text-[#374151]"
            >
              <option value="">{t("analysis.videoReview.chooseImpact")}</option>
              {items.map((i) => (
                <option key={i.key} value={i.key}>
                  {multi ? `${i.src.name} · ` : ""}#{i.rec.impactId} ·{" "}
                  {(i.rec.peakMagG ?? 0).toFixed(1)} g ·{" "}
                  {i.rec.timestampIsEpoch
                    ? `${fmtLocalTime(i.rec.timestampMs)} · `
                    : ""}
                  T+{fmtElapsed(Math.max(0, i.tMs - t0), { compact: true })}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={setSyncHere}
            disabled={!anchorItem}
            title={
              anchorItem
                ? t("analysis.videoReview.lockTitle", {
                    id: anchorItem.rec.impactId,
                    time: fmtVideoTime(now),
                  })
                : t("analysis.videoReview.chooseAnchorFirst")
            }
            className="rounded bg-[#111827] px-2.5 py-1 text-[11px] font-medium text-white transition duration-150 hover:bg-[#374151] disabled:pointer-events-none disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF]"
          >
            {sync
              ? t("analysis.videoReview.resyncAt", { time: fmtVideoTime(now) })
              : t("analysis.videoReview.lockAt", { time: fmtVideoTime(now) })}
          </button>
        </div>
      )}

      {/* Workspace: video left (stays put), impacts right (scroll) */}
      {/* flex-col below lg, grid above: Tailwind classes are compiled, so a
          column width that depends on the footage's aspect has to be inline.
          Portrait phone video in a wide box is mostly letterbox, so it gets a
          narrow column and the list takes the space back. */}
      <div
        className={`flex flex-col lg:grid ${
          fill ? "min-h-0 flex-1 overflow-hidden" : ""
        }`}
        style={{
          gridTemplateColumns: portrait
            ? "360px minmax(0,1fr)"
            : "minmax(0,1.35fr) minmax(0,1fr)",
        }}
      >
        <div
          className={`min-w-0 border-b border-[#F3F4F6] p-3 lg:border-b-0 lg:border-r ${
            fill ? "flex min-h-0 flex-col overflow-y-auto" : ""
          }`}
          style={{ maxWidth: portrait ? 380 : undefined }}
        >
          <video
            ref={videoRef}
            src={src}
            controls
            className="bg-black w-full flex-shrink-0 rounded-md"
            // Filling the pane is the whole point on a wide screen: judging a
            // glancing header off a 320px-tall thumbnail is guesswork.
            style={{ maxHeight: fill ? "min(56vh, 640px)" : 320 }}
            onLoadedMetadata={(e) => {
              setDuration(e.currentTarget.duration);
              setPortrait(
                e.currentTarget.videoHeight > e.currentTarget.videoWidth
              );
            }}
            onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
            onPlay={() => setAutoPaused(null)}
            onSeeked={(e) => {
              // A manual scrub is not a crossing: re-baseline so jumping
              // forward past three impacts does not fire all three.
              prevTRef.current = e.currentTarget.currentTime;
              setAutoPaused(null);
            }}
          >
            <track kind="captions" />
          </video>

          {/* Transport */}
          <div className="mt-1.5 flex flex-shrink-0 flex-wrap items-center gap-1.5">
            <button
              onClick={() => seek((videoRef.current?.currentTime ?? 0) - FRAME)}
              title={t("analysis.videoReview.backFrame")}
              className="flex h-6 w-6 items-center justify-center rounded border border-[#E5E7EB] text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#111827]"
            >
              <SkipBack size={12} />
            </button>
            <button
              onClick={() => seek((videoRef.current?.currentTime ?? 0) + FRAME)}
              title={t("analysis.videoReview.forwardFrame")}
              className="flex h-6 w-6 items-center justify-center rounded border border-[#E5E7EB] text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#111827]"
            >
              <SkipForward size={12} />
            </button>
            <span className="font-mono text-[11px] tabular-nums text-[#6B7280]">
              {fmtVideoTime(now)}
              {Number.isFinite(duration) && (
                <span className="text-[#C4C8CE]">
                  {" "}
                  / {fmtVideoTime(duration)}
                </span>
              )}
            </span>
            {/* The device firing on nothing is only half the error. The other
                half -- a header it never noticed -- has no row to click, so
                without this button it cannot be recorded at all, and recall
                stays unmeasurable. It sits with the transport because that is
                where you are looking when you spot one. */}
            {sync && (
              <button
                onClick={markMissed}
                title={t("analysis.videoReview.logMissedHint")}
                className="flex items-center gap-1 rounded border border-[#DDD6FE] bg-[#F5F3FF] px-1.5 py-0.5 text-[10px] font-medium text-[#6D28D9] transition duration-150 hover:bg-[#EDE9FE]"
              >
                <Flag size={10} />
                {t("analysis.videoReview.missedHeader")}
                <kbd className="text-[9px] opacity-60">M</kbd>
              </button>
            )}
            {sync && (
              <span className="ml-auto flex items-center gap-0.5 rounded-md bg-[#F3F4F6] p-0.5">
                {[
                  {
                    k: "off",
                    Icon: MinusCircle,
                    text: t("common.status.off"),
                    help: t("analysis.videoReview.playModeOffHelp"),
                  },
                  {
                    k: "follow",
                    Icon: Radio,
                    text: t("analysis.videoReview.playModeFollow"),
                    help: t("analysis.videoReview.playModeFollowHelp"),
                  },
                  {
                    k: "stop",
                    Icon: PauseCircle,
                    text: t("common.actions.stop"),
                    help: t("analysis.videoReview.playModeStopHelp"),
                  },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => setPlayMode(m.k)}
                    title={m.help}
                    aria-label={t("analysis.videoReview.onImpactMode", {
                      mode: m.text,
                    })}
                    aria-pressed={playMode === m.k}
                    className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition duration-150 ${
                      playMode === m.k
                        ? "bg-white text-[#111827] shadow-sm"
                        : "text-[#6B7280] hover:text-[#374151]"
                    }`}
                  >
                    <m.Icon size={10} />
                    {m.text}
                  </button>
                ))}
              </span>
            )}
          </div>

          {/* An auto-stop needs to say why the tape stopped, and how to go on.
              Without it the first pause reads as the video having ended. */}
          {autoPaused && (
            <p className="mt-1 flex flex-shrink-0 items-center gap-1.5 rounded bg-[#FFF7ED] px-2 py-1 text-[10px] text-[#B45309]">
              <PauseCircle size={11} className="flex-shrink-0" />
              {t("analysis.videoReview.autoPaused", {
                id: selected?.rec.impactId ?? "?",
              })}
            </p>
          )}

          <div className="mt-1.5 flex flex-shrink-0 flex-wrap items-center gap-1.5">
            {nowSessionMs != null && (
              <span className="text-[10px] tabular-nums text-[#9CA3AF]">
                {/* Where the playhead is in the session -- and, when the
                    device knew the time, on the clock. */}
                {epochItem && (
                  <span className="mr-1.5 text-[#6B7280]">
                    {fmtLocalTime(nowSessionMs)}
                  </span>
                )}
                T+
                {fmtElapsed(Math.max(0, nowSessionMs - t0), { compact: true })}
              </span>
            )}
          </div>

          {/* Verdict — under the video, where your eyes already are */}
          <div className="mt-2 flex-shrink-0">
            {!selected ? (
              <p className="rounded bg-[#F9FAFB] px-2.5 py-2 text-[11px] text-[#6B7280]">
                {t("analysis.videoReview.pickPrompt")}
              </p>
            ) : (
              <>
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="text-[11px] font-semibold text-[#111827]">
                    {multi && (
                      <span
                        className="mr-1.5 rounded px-1 py-px text-[9px]"
                        style={{
                          color: selected.src.color,
                          backgroundColor: hexAlpha(selected.src.color, 0.1),
                        }}
                      >
                        {selected.src.name}
                      </span>
                    )}
                    {t("analysis.videoReview.impactTitle", {
                      id: selected.rec.impactId,
                    })}
                    <span className="ml-1.5 font-normal text-[#9CA3AF]">
                      {(selected.rec.peakMagG ?? 0).toFixed(1)} g
                    </span>
                  </span>
                  <button
                    onClick={nextUnreviewed}
                    className="text-[10px] text-[#6B7280] underline decoration-dotted transition duration-150 hover:text-[#111827]"
                  >
                    {t("analysis.videoReview.nextUnreviewed")}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {VERDICT_ORDER.map((v) => {
                    const Icon = VERDICT_ICON[v];
                    const on = selLabel === v;
                    return (
                      <button
                        key={v}
                        onClick={() => label(v)}
                        aria-label={t("analysis.videoReview.markAs", {
                          verdict: verdictText(t, v),
                        })}
                        className={`flex items-center justify-center gap-1 rounded px-1.5 py-1.5 text-[11px] font-medium transition duration-150 ${
                          on
                            ? "text-white"
                            : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"
                        }`}
                        style={
                          on ? { backgroundColor: VERDICT[v].color } : undefined
                        }
                      >
                        <Icon size={11} />
                        {verdictText(t, v)}
                        <kbd className="text-[9px] opacity-60">
                          {VERDICT[v].key}
                        </kbd>
                      </button>
                    );
                  })}
                </div>
                {/* One sync point lines the FOOTAGE up with the session
                    clock. A unit whose own clock disagrees -- never synced, or
                    synced against a different host -- still needs its own trim
                    on top, and the impact in hand is the evidence for it. */}
                {multi && selected.src.onOffset && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 border-t border-[#F3F4F6] pt-1">
                    <button
                      onClick={alignSelectedUnit}
                      title={t("analysis.videoReview.alignHint", {
                        name: selected.src.name,
                      })}
                      className="rounded border border-[#E5E7EB] bg-white px-1.5 py-0.5 text-[10px] font-medium text-[#374151] transition duration-150 hover:bg-[#F3F4F6]"
                    >
                      {t("analysis.videoReview.alignButton", {
                        name: selected.src.name,
                      })}
                    </button>
                    {!!selected.src.offsetMs && (
                      <span className="flex items-center gap-1 text-[10px] tabular-nums text-[#9CA3AF]">
                        {t("analysis.videoReview.offsetValue", {
                          sign: selected.src.offsetMs > 0 ? "+" : "−",
                          value: fmtElapsed(Math.abs(selected.src.offsetMs), {
                            compact: true,
                          }),
                        })}
                        <button
                          onClick={() => selected.src.onOffset(0)}
                          title={t("analysis.videoReview.clearOffset")}
                          className="text-[#C4C8CE] transition duration-150 hover:text-[#DC2626]"
                        >
                          <X size={9} />
                        </button>
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between">
                  <button
                    onClick={markEarlierPreSession}
                    className="text-[10px] text-[#6B7280] underline decoration-dotted transition duration-150 hover:text-[#111827]"
                  >
                    {multi
                      ? t("analysis.videoReview.markEarlierPreSessionUnit")
                      : t("analysis.videoReview.markEarlierPreSession")}
                  </button>
                  <span className="text-[10px] text-[#C4C8CE]">
                    {t("analysis.videoReview.shortcuts")}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Missed header */}
          {sync && (
            <div className="mt-2 flex-shrink-0 border-t border-[#F3F4F6] pt-2">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                <Flag size={10} />
                {t("analysis.videoReview.missedSectionTitle")}
                {multi && (
                  <span className="font-normal normal-case tracking-normal">
                    {t("analysis.videoReview.loggedAgainst", {
                      name: (selected?.src ?? srcs[0]).name,
                    })}
                  </span>
                )}
              </p>
              <div className="mt-1 flex gap-1">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("analysis.videoReview.notePlaceholder")}
                  className="min-w-0 flex-1 rounded border border-[#E5E7EB] px-1.5 py-1 text-[11px]"
                />
                <button
                  onClick={markMissed}
                  className="flex-shrink-0 rounded border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F3F4F6]"
                >
                  {t("analysis.videoReview.markAt", {
                    time: fmtVideoTime(now),
                  })}
                </button>
              </div>
              {/* The marks themselves live in the list, in time order among
                  the records -- a checklist with a hole in it reads as a hole.
                  Repeating them here as chips put two Remove buttons on screen
                  for the same entry. */}
              {missed.length > 0 && (
                <p className="mt-1 text-[10px] text-[#9CA3AF]">
                  {t("analysis.videoReview.missedLogged", { n: missed.length })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Impacts — its own scroll area, so the video never moves while you
            work down the list. */}
        <div className="flex min-h-0 min-w-0 flex-col">
          <div className="flex-shrink-0 border-b border-[#F3F4F6] px-2.5 py-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-0.5 rounded-md bg-[#F3F4F6] p-0.5">
                {[
                  {
                    k: "todo",
                    text: t("analysis.videoReview.filterTodo", {
                      n: progress.remaining,
                    }),
                  },
                  {
                    k: "headers",
                    text: t("analysis.videoReview.filterHeaders", {
                      n: progress.headers,
                    }),
                  },
                  {
                    k: "all",
                    text: t("analysis.videoReview.filterAll", {
                      n: items.length,
                    }),
                  },
                ].map((f) => (
                  <button
                    key={f.k}
                    onClick={() => setFilter(f.k)}
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition duration-150 ${
                      filter === f.k
                        ? "bg-white text-[#111827] shadow-sm"
                        : "text-[#6B7280] hover:text-[#374151]"
                    }`}
                  >
                    {f.text}
                  </button>
                ))}
              </div>
              {/* With five units the merged list is five times as long. Being
                  able to walk one athlete at a time is what keeps it usable. */}
              {multi && (
                <select
                  value={unitFilter}
                  aria-label={t("analysis.videoReview.showUnit")}
                  onChange={(e) => setUnitFilter(e.target.value)}
                  className="rounded border border-[#E5E7EB] bg-white px-1.5 py-0.5 text-[10px] text-[#374151]"
                >
                  <option value="all">
                    {t("analysis.videoReview.allUnits")}
                  </option>
                  {srcs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* The dry run's whole purpose, kept in view while you work: does
                the firmware's verdict match what the video shows? */}
            {score.scored > 0 && (
              <p className="mt-1 text-[10px] text-[#9CA3AF]">
                <Trans
                  i18nKey="analysis.videoReview.gateAgrees"
                  values={{ agree: score.agree, scored: score.scored }}
                  components={{
                    bold: <span className="font-semibold text-[#374151]" />,
                  }}
                />
                {score.wouldDrop > 0 && (
                  <>
                    {" · "}
                    <span className="font-semibold text-[#B45309]">
                      {t("analysis.videoReview.wouldDrop", {
                        count: score.wouldDrop,
                      })}
                    </span>
                  </>
                )}
                {score.wouldKeep > 0 && (
                  <>
                    {" · "}
                    <span className="text-[#6B7280]">
                      {t("analysis.videoReview.wouldKeep", {
                        n: score.wouldKeep,
                      })}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
          {/* A column per file. One merged list buried the thing that matters
              most -- two units firing at the same instant -- in an interleaved
              run of rows; side by side it is a glance. */}
          <div
            ref={listRef}
            className={`flex ${
              fill ? "min-h-0 flex-1" : "max-h-[420px]"
            } divide-x divide-[#F3F4F6] overflow-x-auto`}
          >
            {columns.map((col) => (
              <div
                key={col.src.id}
                role="group"
                aria-label={col.src.name || t("common.entities.impact_other")}
                className="flex min-h-0 flex-1 flex-col"
                style={{ minWidth: columns.length > 1 ? 260 : 0 }}
              >
                {columns.length > 1 && (
                  <div
                    className="flex flex-shrink-0 items-center gap-1.5 border-b border-[#F3F4F6] px-2.5 py-1"
                    style={{ backgroundColor: hexAlpha(col.src.color, 0.06) }}
                  >
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: col.src.color }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-[#111827]">
                      {col.src.name}
                    </span>
                    <span className="flex-shrink-0 text-[10px] tabular-nums text-[#9CA3AF]">
                      {col.items.length}
                      {col.rows.length > col.items.length && (
                        <span className="ml-1 text-[#7C3AED]">
                          +{col.rows.length - col.items.length}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {col.rows.length === 0 && (
                    <p className="px-2.5 py-4 text-center text-[11px] text-[#9CA3AF]">
                      {filter === "todo"
                        ? t("analysis.videoReview.allChecked")
                        : t("analysis.videoReview.nothingYet")}
                    </p>
                  )}
                  {col.rows.map((row) =>
                    row.missedEntry ? (
                      <MissedRow
                        key={`missed:${row.src.id}:${row.index}`}
                        entry={row}
                        t0={t0}
                        onPick={(m) => seek(videoTimeForSessionMs(m.tMs, sync))}
                        onRemove={() =>
                          row.src.onReview((r) => ({
                            ...r,
                            missed: r.missed.filter((_, j) => j !== row.index),
                          }))
                        }
                      />
                    ) : (
                      <ImpactRow
                        key={row.key}
                        item={row}
                        t0={t0}
                        multi={multi}
                        showUnit={false}
                        live={liveKeys.has(row.key)}
                        verdict={
                          row.src.review.labels[recordKey(row.rec)]?.label
                        }
                        active={row.key === selKey}
                        onPick={pick}
                      />
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ReviewTimeline
        items={items}
        sources={srcs}
        missed={missed}
        activeKey={selKey}
        playheadMs={nowSessionMs}
        onPick={pick}
      />
    </div>
  );
};

export default VideoReview;
